import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Input,
  SimpleGrid,
  Text,
  useColorMode,
} from '@chakra-ui/react';
import IconRemoveOption from 'app/assets/svgs/IconRemoveOption';
import IconReturn from 'app/assets/svgs/IconReturn';
import { BPM_MAX, BPM_MIN, YEAR_MIN } from 'app/constants';
import { MEDIA_TYPE, SORT_TYPE } from 'app/constants/enum';
import { useMediaScreen } from 'app/hooks/mediaScreen/useMediaScreen';
import { useSections } from 'app/hooks/sections/useSections';
import { useTracksKeys } from 'app/hooks/trackKeys/useTrackKeys';
import { Genre, Tag, Users } from 'app/models';
import classNames from 'classnames';
import { useCallback, useMemo } from 'react';
import SelectItem from '../Select';
import styles from './filterSelect.module.scss';
import { usePlayers } from 'app/hooks/player/usePlayers';
import { onlyShowNumber } from 'app/utils/onlyShowNumber';

interface Props {
  filterItems?: any;
  filter?: any;
  onFilter?: (event, key, type) => void;
  genres?: Genre[];
  tags?: Tag[];
  users?: Users[];
  onReset?: () => void;
  setFilter?: any;
  isShowPageTrack?: boolean;
  handleFilterBpmOrYear?: any;
  range?: any;
  sortOptions?: Array<{ label: string; value: string | number }>;
  initialFilter?: any;
  ignoreLabelFilter?: boolean;
}

export default function FilterSelect({
  filterItems = [],
  filter,
  onFilter,
  genres,
  tags,
  users,
  onReset,
  setFilter,
  isShowPageTrack = false,
  handleFilterBpmOrYear,
  range,
  sortOptions = [],
  initialFilter,
  ignoreLabelFilter = false,
}: Props) {
  const { sections } = useSections();
  const { trackKeys } = useTracksKeys();
  const { colorMode } = useColorMode();
  const { isLargerThan992, isLargerThan650 } = useMediaScreen();
  const { handleInputFocus, handleInputBlur } = usePlayers();

  const currentYear = new Date().getFullYear();

  const defaultSort = initialFilter?.sort || 'publishDate@desc';
  const isSortActive = filter.sort && filter.sort !== defaultSort;

  const isShowListOptionSelected =
    (filter.showGenres && filter.showGenres.length > 0) ||
    (filter.showTags && filter.showTags.length > 0) ||
    (filter.showContributors && filter.showContributors.length > 0) ||
    (filter.showTrackKeys && filter.showTrackKeys.length > 0) ||
    filter.showAudio === false ||
    filter.showVideo === false ||
    filter.clean === false ||
    filter.dirty === false ||
    filter.showFavoriteByMe === true ||
    (filter.search && filter.search.length > 0) ||
    (range?.bpmStart && range.bpmStart !== BPM_MIN) ||
    (range?.bpmEnd && range.bpmEnd !== BPM_MAX) ||
    (range?.yearFrom && range.yearFrom !== YEAR_MIN) ||
    (range?.yearTo && range.yearTo !== currentYear) ||
    (!ignoreLabelFilter && isShowPageTrack && filter.labelId) ||
    isSortActive;

  const itemFilter = useMemo(() => {
    return [
      SORT_TYPE.SHOW_TAGS,
      SORT_TYPE.SHOW_GENRES,
      SORT_TYPE.SHOW_SUB_GENRES,
      SORT_TYPE.SHOW_CONTRIBUTORS,
      SORT_TYPE.SHOW_SECTIONS,
      SORT_TYPE.SHOW_TRACKKEYS,
    ];
  }, []);

  const handleCheckKey = (item, key) => {
    return item.includes(key);
  };

  const handleValue = useCallback(
    item => {
      return item.opions.filter(i => {
        if (filter && filter[item.name]) {
          // if (
          //   [
          //     // SORT_TYPE.SHOW_SECTIONS,
          //     // SORT_TYPE.SHOW_TRACKKEYS,
          //     // SORT_TYPE.SHOW_CONTRIBUTORS,
          //   ].includes(item.name)
          // ) {
          //   return i.value === filter[item.name].value;
          // }

          return filter[item.name].some(j => j.value === i.value);
        }
        return false;
      });
    },
    [filter],
  );

  const handleShowTag = useCallback(
    (filters, key, i) => {
      return (
        <Box>
          {/* <Text fontSize="12px" m="5px 5px 5px 0">
            {key === SORT_TYPE.SHOW_GENRES && 'Filter Genres'}
            {key === SORT_TYPE.SHOW_SUB_GENRES && 'Filter Sub Genres'}
            {key === SORT_TYPE.SHOW_TAGS && 'Filter Tags'}
            {key === SORT_TYPE.SHOW_TRACKKEYS && 'Filter Keys'}
            {key === SORT_TYPE.SHOW_CONTRIBUTORS && 'Filter Contributons'}
            {key === SORT_TYPE.SHOW_SECTIONS && 'Filter Labels'}
          </Text> */}
          <Flex className={styles.optionItem} key={i.value} alignItems="center">
            <IconRemoveOption
              onClick={() => {
                const data = filters[key]?.filter(item => item !== i);
                const newData = data.map(item => {
                  return {
                    label: item.name,
                    value: item._id,
                  };
                });
                onFilter && onFilter(newData, key, 'dropdown');
              }}
            />
            <Text fontSize="12px" ml="5px">
              {/* {key === SORT_TYPE.SHOW_SUB_GENRES && 'Sub Genres:'} */}
              {i?.subGenre && 'Sub Genres: '}
              {key === SORT_TYPE.SHOW_GENRES && !i?.subGenre && 'Genres:'}
              {key === SORT_TYPE.SHOW_TRACKKEYS && 'Keys:'}
              {key === SORT_TYPE.SHOW_CONTRIBUTORS && 'Contributors:'}
              {key === SORT_TYPE.SHOW_SECTIONS && 'Labels:'}
              {key === SORT_TYPE.SHOW_TAGS && 'Tags:'}
            </Text>
            <Text fontSize="12px" ml="5px">
              {i.label ||
                i.name ||
                i?.username ||
                (i?.musicKey === 'None'
                  ? 'None'
                  : `${i?.musicKey} / ${i?.camelotKey}`)}
            </Text>
          </Flex>
        </Box>
      );
    },
    [onFilter],
  );

  const handleShowCheckboxFilter = useCallback(
    (key: string, value: boolean) => {
      return (
        <Box>
          <Flex className={styles.optionItem} alignItems="center">
            <IconRemoveOption
              onClick={() => {
                // Create a synthetic event object to match handleChangeFilter's default case
                const syntheticEvent = {
                  target: {
                    name: key,
                  },
                };
                onFilter && onFilter(syntheticEvent, key, 'checkbox');
              }}
            />
            <Text fontSize="12px" ml="5px">
              {key === 'showAudio' && 'Audio:'}
              {key === 'showVideo' && 'Video:'}
              {key === 'clean' && 'Clean:'}
              {key === 'dirty' && 'Dirty:'}
              {key === 'showFavoriteByMe' && 'Favorites:'}
            </Text>
            <Text fontSize="12px" ml="5px">
              {key === 'showFavoriteByMe' ? 'Only' : value ? 'Only' : 'Hidden'}
            </Text>
          </Flex>
        </Box>
      );
    },
    [onFilter],
  );

  const handleShowOtherFilter = useCallback(
    (label: string, value: string, onRemove: () => void) => {
      return (
        <Box>
          <Flex className={styles.optionItem} alignItems="center">
            <IconRemoveOption onClick={onRemove} />
            <Text fontSize="12px" ml="5px">
              {label}:
            </Text>
            <Text fontSize="12px" ml="5px">
              {value}
            </Text>
          </Flex>
        </Box>
      );
    },
    [],
  );

  const handleShowSelected = useCallback(
    (filters, key) => {
      return handleCheckKey(itemFilter, key)
        ? filters[key]?.length > 0 &&
            filters[key].map(i => handleShowTag(filters, key, i))
        : '';
    },
    [handleShowTag, itemFilter],
  );

  const renderListOptionSelected = useCallback(
    (genres, tags, users) => {
      const showSections = (sections || []).filter(tag => {
        return (filter.showSections || []).some(item => {
          return tag._id === item.value;
        });
      });

      const showTrackKeys = (trackKeys || []).filter(tag => {
        return (filter.showTrackKeys || []).some(item => {
          return tag._id === item.value;
        });
      });

      const showContributors = (users || []).filter(tag => {
        return (filter.showContributors || []).some(item => {
          return tag._id === item.value;
        });
      });

      const showGenres = (genres || []).filter(gen => {
        return (filter.showGenres || []).some(item => {
          return gen._id === item.value;
        });
      });

      let subGenre = showGenres;
      if (showGenres.length === 2 && filter.typeFilter === 'all') {
        subGenre = showGenres.map((item, i) => {
          if (i === 1) {
            return { ...item, subGenre: true };
          }
          return item;
        });
      }
      // const showSubGenres = (genres || []).filter(gen => {
      //   return (filter.showSubGenres || []).some(item => {
      //     return gen._id === item.value;
      //   });
      // });

      const showTags = (tags || []).filter(tag => {
        return (filter.showTags || []).some(item => {
          return tag._id === item.value;
        });
      });

      const filters = {
        ...filter,
        showGenres: subGenre,
        showTags,
        // showSubGenres,
        showContributors,
        showSections,
        showTrackKeys,
      };

      const result: any[] = [];

      // Render sort filter if different from default
      if (isSortActive) {
        const sortOption = sortOptions.find(opt => opt.value === filter.sort);
        if (sortOption) {
          result.push(
            handleShowOtherFilter('Sort By', sortOption.label, () => {
              onFilter && onFilter(defaultSort, 'sort', 'dropdown');
            }),
          );
        }
      }

      // Render search filter if present
      if (filter.search && filter.search.length > 0) {
        result.push(
          handleShowOtherFilter('Search', `"${filter.search}"`, () => {
            // Use empty string to clear search via handleChangeFilter
            onFilter && onFilter('', 'search', 'search_clear');
          }),
        );
      }

      // Render label filter if present (only for tracks page)
      if (!ignoreLabelFilter && isShowPageTrack && filter.labelId) {
        const selectedLabel = (sections || []).find(
          s => s._id === filter.labelId,
        );
        if (selectedLabel) {
          result.push(
            handleShowOtherFilter('Label', selectedLabel.name, () => {
              onFilter && onFilter('', 'labelId', 'dropdown');
            }),
          );
        }
      }

      // Render BPM range filter if different from default
      const currentYear = new Date().getFullYear();
      if (
        (range?.bpmStart && range.bpmStart !== BPM_MIN) ||
        (range?.bpmEnd && range.bpmEnd !== BPM_MAX)
      ) {
        const bpmStart = range?.bpmStart || BPM_MIN;
        const bpmEnd = range?.bpmEnd || BPM_MAX;
        result.push(
          handleShowOtherFilter('BPM', `${bpmStart} - ${bpmEnd}`, () => {
            handleFilterBpmOrYear && handleFilterBpmOrYear('bpmStart', BPM_MIN);
            handleFilterBpmOrYear && handleFilterBpmOrYear('bpmEnd', BPM_MAX);
          }),
        );
      }

      // Render Year range filter if different from default
      if (
        (range?.yearFrom && range.yearFrom !== YEAR_MIN) ||
        (range?.yearTo && range.yearTo !== currentYear)
      ) {
        const yearFrom = range?.yearFrom || YEAR_MIN;
        const yearTo = range?.yearTo || currentYear;
        result.push(
          handleShowOtherFilter('Year', `${yearFrom} - ${yearTo}`, () => {
            handleFilterBpmOrYear &&
              handleFilterBpmOrYear('yearFrom', YEAR_MIN);
            handleFilterBpmOrYear &&
              handleFilterBpmOrYear('yearTo', currentYear);
          }),
        );
      }

      // Render standard filters (genres, tags, contributors, etc.)
      Object.keys(filters).forEach(function (key) {
        const filterResult = handleShowSelected(filters, key);
        if (filterResult) {
          result.push(filterResult);
        }
      });

      // Render checkbox filters if they differ from default
      if (filter.showAudio === false) {
        result.push(handleShowCheckboxFilter('showAudio', false));
      }
      if (filter.showVideo === false) {
        result.push(handleShowCheckboxFilter('showVideo', false));
      }
      if (filter.clean === false) {
        result.push(handleShowCheckboxFilter('clean', false));
      }
      if (filter.dirty === false) {
        result.push(handleShowCheckboxFilter('dirty', false));
      }
      if (filter.showFavoriteByMe === true) {
        result.push(handleShowCheckboxFilter('showFavoriteByMe', true));
      }

      return result;
    },
    [
      filter,
      handleShowSelected,
      handleShowCheckboxFilter,
      handleShowOtherFilter,
      sections,
      trackKeys,
      range,
      isShowPageTrack,
      handleFilterBpmOrYear,
      onFilter,
      isSortActive,
      sortOptions,
      defaultSort,
      ignoreLabelFilter,
    ],
  );

  return (
    <Box w="100%" className={classNames(styles.lbHeader, styles[colorMode])}>
      <Flex
        w="100%"
        gridGap="20px"
        flexWrap={{ base: 'wrap', md: 'wrap', lg: 'unset' }}
        justifyContent={{ base: 'end', sm: 'end', md: 'end', lg: 'unset' }}
        position="relative"
      >
        <SimpleGrid
          display={isShowPageTrack && isLargerThan992 ? 'flex' : 'grid'}
          w="100%"
          gridGap="6px"
          columns={1}
          padding="5px"
          border={`${isShowPageTrack ? 0 : 1}px solid #ebe3e3`}
          borderRadius="5px"
        >
          {filterItems.map((item, idx) => {
            return (
              <Box key={idx} w="100%">
                {item.name !== MEDIA_TYPE.CHECKBOX && (
                  <SelectItem
                    options={item.opions}
                    label={item.label}
                    name={item.name}
                    multi={item.multi}
                    onFilter={onFilter}
                    all={item.all}
                    value={handleValue(item)}
                  />
                )}
              </Box>
            );
          })}
        </SimpleGrid>
      </Flex>
      <Flex gridGap="10px" flexDirection={isLargerThan650 ? 'row' : 'column'}>
        <FormControl mb={2}>
          <FormLabel
            fontSize="12px"
            fontWeight={700}
            textDecoration="underline"
            my="4px"
          >
            Tempo range (0 - 220)
          </FormLabel>
          <Flex gridGap="10px">
            <Input
              borderRadius="6px"
              bg="#fff"
              w="100%"
              placeholder="From bpm"
              _placeholder={{ opacity: 1, color: '#000' }}
              color="#000"
              fontSize="14px"
              type="number"
              onChange={e => {
                if (
                  e.target.value.length > 1 &&
                  e.target.value.startsWith('0')
                ) {
                  e.target.value = e.target.value.slice(1);
                }
                handleFilterBpmOrYear('bpmStart', Number(e.target.value));
              }}
              value={range?.bpmStart && Number(range?.bpmStart)}
              onKeyDown={e => onlyShowNumber(e)}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
            ></Input>
            <Input
              borderRadius="6px"
              bg="#fff"
              w="100%"
              placeholder="To bpm"
              _placeholder={{ opacity: 1, color: '#000' }}
              color="#000"
              fontSize="14px"
              onChange={e =>
                handleFilterBpmOrYear('bpmEnd', Number(e.target.value))
              }
              value={range?.bpmEnd && Number(range?.bpmEnd)}
              onKeyDown={e => onlyShowNumber(e)}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
            ></Input>
          </Flex>
        </FormControl>
        <FormControl mb={2}>
          <FormLabel
            fontSize="12px"
            fontWeight={700}
            textDecoration="underline"
            my="4px"
          >
            Release Year Range (1950 - {new Date().getFullYear()})
          </FormLabel>
          <Flex gridGap="10px">
            <Input
              borderRadius="6px"
              bg="#fff"
              w="100%"
              placeholder="From year"
              _placeholder={{ opacity: 1, color: '#000' }}
              color="#000"
              fontSize="14px"
              onChange={e =>
                handleFilterBpmOrYear('yearFrom', Number(e.target.value))
              }
              value={range?.yearFrom && Number(range?.yearFrom)}
              onKeyDown={e => onlyShowNumber(e)}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
            ></Input>

            <Input
              borderRadius="6px"
              bg="#fff"
              w="100%"
              placeholder="To year"
              _placeholder={{ opacity: 1, color: '#000' }}
              color="#000"
              fontSize="14px"
              onChange={e =>
                handleFilterBpmOrYear('yearTo', Number(e.target.value))
              }
              value={range?.yearTo && Number(range?.yearTo)}
              onKeyDown={e => onlyShowNumber(e)}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
            ></Input>
          </Flex>
        </FormControl>
      </Flex>
      {isShowListOptionSelected && (
        <Flex
          alignItems="center"
          justifyContent="space-between"
          m="15px 5px 5px 0"
        >
          <Text fontSize="12px" fontWeight={700}>
            Active Filters
          </Text>
          <Flex
            cursor="pointer"
            onClick={() => onReset?.()}
            alignItems="center"
          >
            <IconReturn />
          </Flex>
        </Flex>
      )}
      {isShowListOptionSelected && (
        <Flex flexWrap="wrap">
          {renderListOptionSelected(genres, tags, users)}
        </Flex>
      )}
    </Box>
  );
}
